import React, { useState, useEffect } from "react";
import '../pages/App.css';
import { baseUrl } from '../shared';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CategoryIcon from '@mui/icons-material/Category';
import QrScanDialog from './QrScanDialog';
import { StatusChip } from './ui';

export function Search() {
  const [scanOpen, setScanOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null); // list of items for a chosen type

  const authHeaders = () => ({
    username: localStorage.getItem('username'),
    bToken: localStorage.getItem('bToken'),
  });

  const goToItem = (value) => {
    const v = (value || '').trim();
    if (!v) return;
    window.location.href = '/?item=' + encodeURIComponent(v);
  };

  const handleScan = ({ itemNo, raw }) => {
    setScanOpen(false);
    goToItem(itemNo || raw);
  };

  // Load item types once (used by the "Search by Item Type" dialog).
  useEffect(() => {
    fetch(baseUrl + 'items/types/', { headers: authHeaders() })
      .then((r) => r.json())
      .then((json) => setTypes(Array.isArray(json) ? json : []))
      .catch(() => {});
  }, []);

  const openTypeSearch = () => { setSelectedType(null); setTypeOpen(true); };

  const showItemsOfType = async (typeName) => {
    try {
      const res = await fetch(baseUrl + 'items/type/', {
        headers: { ...authHeaders(), itemType: typeName },
      });
      const json = await res.json();
      setSelectedType(Array.isArray(json) ? json : []);
    } catch (_) { /* ignore */ }
  };

  return (
    <Box sx={{ width: 'min(460px, calc(100vw - 32px))', mx: 'auto', mt: 1 }}>
      <Box sx={{ background: 'var(--surface)', borderRadius: 'var(--r)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', p: 2.5 }}>
        <Box sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', mb: 0.5 }}>Find an item</Box>
        <Box sx={{ fontSize: 12, color: 'var(--muted)', mb: 2 }}>
          Search by item number, browse by type, or scan the item's QR tag.
        </Box>

        <TextField
          fullWidth
          margin="dense"
          id="itemInput"
          label="Item Number"
          variant="outlined"
          type="text"
          onKeyDown={(e) => { if (e.keyCode === 13) { e.preventDefault(); goToItem(document.getElementById('itemInput').value); } }}
        />
        <Button fullWidth variant="contained" sx={{ mt: 0.5 }} onClick={() => goToItem(document.getElementById('itemInput').value)}>
          Search
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5, color: 'var(--faint)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px' }}>
          <Box sx={{ flex: 1, height: '1px', background: 'var(--border)' }} /> or <Box sx={{ flex: 1, height: '1px', background: 'var(--border)' }} />
        </Box>

        {/* Search by Item Type (85%) + QR scan (15%), like the AIMS serial+QR row. */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button sx={{ flex: '1 1 85%' }} variant="outlined" startIcon={<CategoryIcon />} onClick={openTypeSearch}>
            Search by Item Type
          </Button>
          <Tooltip title="Scan item QR">
            <IconButton
              aria-label="Scan item QR"
              onClick={() => setScanOpen(true)}
              sx={{
                flex: '1 1 15%', borderRadius: 'var(--r-sm, 8px)',
                border: '1px solid var(--border)', color: 'var(--primary)',
                '&:hover': { borderColor: 'var(--primary)', background: 'var(--primary-tint)' },
              }}
            >
              <QrCodeScannerIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Item-type search dialog */}
      <Dialog open={typeOpen} onClose={() => setTypeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedType ? 'Select an item' : 'Search by Item Type'}</DialogTitle>
        <DialogContent dividers>
          {!selectedType ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {types.map((t, i) => (
                <Button key={i} variant="outlined" onClick={() => showItemsOfType(t.type)}>{t.type}</Button>
              ))}
              {types.length === 0 && <Box sx={{ color: 'var(--muted)', fontSize: 13 }}>No item types available.</Box>}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {selectedType.map((it, i) => (
                <Box
                  key={i}
                  onClick={() => { window.location.href = '?item=' + it.itemNo; }}
                  sx={{ cursor: 'pointer', p: 1.25, borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface)', '&:hover': { borderColor: 'var(--primary)' } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <StatusChip domain="booking" value={it.status} dot />
                    <Box className="app-mono" sx={{ fontWeight: 700, fontSize: 14 }}>{it.itemNo}</Box>
                    <Box sx={{ fontSize: 13, color: 'var(--text)' }}>{it.name}</Box>
                  </Box>
                  <Box sx={{ fontSize: 11.5, color: 'var(--muted)', mt: 0.25 }}>
                    Cat No: {it.catNo} · Asset No: {it.assetId} · {it.company === null ? 'Available' : 'Company: ' + it.company}
                  </Box>
                </Box>
              ))}
              {selectedType.length === 0 && <Box sx={{ color: 'var(--muted)', fontSize: 13 }}>No items of this type.</Box>}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {selectedType ? <Button onClick={() => setSelectedType(null)} sx={{ textTransform: 'none' }}>Back to types</Button> : <span />}
          <Button onClick={() => setTypeOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <QrScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onResult={handleScan} />
    </Box>
  );
}

export default Search;
